import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { ChartMountOptions, colorFor, getChartTheme, getDescriptionForVariant, getPaletteForVariant, getPieTitleForVariant } from './chartTheme';
import { exportChartAsImage } from './chartExport';

ChartJS.register(ArcElement, Tooltip, Legend);

type EndpointResponse = {
  year?: number | string | null;
  labels: Array<number | string>;
  datasets: Array<{
    label?: string;
    data: number[];
  }>;
};

type PieVariantKey = 'bereich' | 'incomegroups' | 'financingtool';

type PieVariant = {
  key: PieVariantKey;
  label: string;
  endpoint: string;
};

const PIE_VARIANTS: PieVariant[] = [
  {
    key: 'bereich',
    label: 'Bereich',
    endpoint: '/infographics/v1/latest_bereich'
  },
  {
    key: 'incomegroups',
    label: 'Weltbank-Einkommensgruppe',
    endpoint: '/infographics/v1/latest_incomegroups'
  },
  {
    key: 'financingtool',
    label: 'Finanzierungsinstrument',
    endpoint: '/infographics/v1/latest_financingtool'
  }
];

export type InfographicsPieChartProps = Omit<ChartMountOptions, 'chartType'>;

function getOrigin(endpoint: string): string {
  try {
    return new URL(endpoint, window.location.href).origin;
  } catch {
    return '';
  }
}

function getInitialVariant(endpoint: string): PieVariantKey {
  let path = endpoint;
  try {
    path = new URL(endpoint, window.location.href).pathname;
  } catch {
    // Keep the raw endpoint if it isn't a valid URL (e.g. a bare relative path).
  }
  return PIE_VARIANTS.find((variant) => variant.endpoint === path)?.key ?? 'bereich';
}

function wrapLabel(text: string, maxLen: number): string[] {
  if (text.length <= maxLen) return [text];
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (current.length === 0) {
      current = word;
    } else if (current.length + 1 + word.length <= maxLen) {
      current += ' ' + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

export function InfographicsPieChart({ endpoint, height = 480, locale = 'de-DE' }: InfographicsPieChartProps) {
  const [data, setData] = useState<EndpointResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [variant, setVariant] = useState<PieVariantKey>(() => getInitialVariant(endpoint));
  const [dataYear, setDataYear] = useState<number | null>(null);
  const chartRef = useRef<ChartJS | null>(null);

  const theme = useMemo(() => getChartTheme(), []);

  const origin = useMemo(() => getOrigin(endpoint), [endpoint]);
  const currentVariant = PIE_VARIANTS.find((entry) => entry.key === variant) ?? PIE_VARIANTS[0];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(origin + currentVariant.endpoint, { credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return (await response.json()) as EndpointResponse;
      })
      .then((json) => {
        if (cancelled) {
          return;
        }
        setData(json);
        const parsedYear = typeof json.year === 'number' ? json.year : Number(json.year);
        setDataYear(Number.isFinite(parsedYear) ? parsedYear : null);
      })
      .catch((reason: unknown) => {
        if (cancelled) {
          return;
        }
        setError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [origin, currentVariant.endpoint]);

  const chartData = useMemo(() => {
    if (!data || data.labels.length === 0 || data.datasets.length === 0) {
      return null;
    }

    const dataset = data.datasets[0];

    return {
      labels: data.labels.map(String),
      datasets: [
        {
          label: dataset.label ?? currentVariant.label,
          data: dataset.data,
          backgroundColor: data.labels.map((label, index) => {
            const palette = getPaletteForVariant(variant);
            return palette[index % palette.length] ?? colorFor(String(label));
          }),
          borderColor: '#ffffff',
          borderWidth: 2
        }
      ]
    };
  }, [currentVariant.label, data, theme, variant]);

  const options = useMemo(() => {
    const currencyFormat = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    });

    const mrdFormat = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    const total = chartData?.datasets[0]?.data.reduce((sum, value) => sum + Number(value ?? 0), 0) ?? 0;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'left' as const,
          align: 'start' as const,
          labels: {
            color: theme.textColor,
            font: {
              family: theme.fontFamily
            },
            padding: 16,
            generateLabels: (chart: ChartJS) => {
              const dataset = chart.data.datasets[0];
              return (chart.data.labels ?? []).map((label, i) => {
                const value = Number((dataset?.data[i] as number) ?? 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                const meta = chart.getDatasetMeta(0);
                const hidden = (meta.data[i] as any)?.hidden ?? false;
                return {
                  text: [...wrapLabel(String(label), 25), `${mrdFormat.format(value / 1e9)} Mrd. € (${percentage}%)`] as any,
                  fillStyle: (dataset?.backgroundColor as string[])?.[i] ?? '#ccc',
                  strokeStyle: '#ffffff',
                  lineWidth: 2,
                  hidden,
                  index: i
                };
              });
            }
          }
        },
        tooltip: {
          titleFont: {
            family: theme.fontFamily
          },
          bodyFont: {
            family: theme.fontFamily
          },
          callbacks: {
            label: (context: any) => {
              const value = Number(context.raw ?? 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
              return `${context.label}: ${mrdFormat.format(value / 1e9)} Mrd. € (${percentage}%)`;
            }
          }
        }
      }
    };
  }, [chartData, locale, theme]);

  if (loading) {
    return <div>Diagramm wird geladen…</div>;
  }

  if (error) {
    return <div>Fehler beim Laden: {error}</div>;
  }

  if (!chartData) {
    return <div>Keine Daten.</div>;
  }

  const title = getPieTitleForVariant(variant, dataYear ?? new Date().getFullYear());
  const description = getDescriptionForVariant(variant);
  const handleDownload = () => {
    const year = dataYear ?? new Date().getFullYear();
    exportChartAsImage({ chart: chartRef.current, fileName: `klimafinanzierung-${year}-${variant}.png` });
  };

  return (
    <div>
      {title && (
        <h2 className="ddk-font--base" style={{ margin: '0 0 0.75rem', padding: '0 1rem' }}>
          {title}
        </h2>
      )}
      <div
        style={{
          display: 'flex',
          gap: '2rem',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          padding: '0 1rem'
        }}
      >
        <div
          className="ddk-font--base"
          style={{
            minWidth: '220px',
            maxWidth: '320px',
            margin: '0 0.5rem'
          }}
        >
          <label className="ddk-font--base" style={{ display: 'block', marginBottom: '0.25rem' }}>
            Aufschlüsseln nach:
          </label>
          <select
            className="ddk-font--base"
            value={variant}
            onChange={(event) => {
              setVariant(event.target.value as PieVariantKey);
            }}
          >
            {PIE_VARIANTS.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ height }}>
        <Pie ref={chartRef as any} data={chartData as any} options={options as any} />
      </div>
      <div style={{ textAlign: 'right', padding: '0.25rem 1rem 0' }}>
        <button
          type="button"
          onClick={handleDownload}
          title="Als Bild herunterladen"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: theme.controlColor }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
      </div>
      {description && (
        <p className="ddk-font--base" style={{ margin: '0.75rem 0 0', padding: '0 1rem' }}>
          {description}
        </p>
      )}
    </div>
  );
}
